import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';

/**
 * Drives an upstream `CreateNewContactDialog`: opens it with a clean form and
 * persists what it emits, alerting and redirecting to the new contact.
 *
 * Used by the v4 sidebar and the mobile search (`SearchView.vue`). The form ref
 * is null until the dialog opens (the Dialog mounts its content on open), so
 * resetting validation must stay optional.
 *
 * @param {import('vue').Ref} dialogRef - ref on the CreateNewContactDialog instance.
 * @returns {Object} `open` and `create` handlers for the dialog.
 */
export function useCreateContact(dialogRef) {
  const store = useStore();
  const router = useRouter();
  const route = useRoute();
  const { t } = useI18n();

  const open = async () => {
    await dialogRef.value?.contactsFormRef?.resetValidation();
    dialogRef.value?.dialogRef?.open();
  };

  const create = async contact => {
    try {
      const { id } = await store.dispatch('contacts/create', contact);
      dialogRef.value?.onSuccess();
      useAlert(
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE')
      );
      // Zimobi: redirect to the newly created contact.
      router.push(`/app/accounts/${route.params.accountId}/contacts/${id}`);
    } catch (error) {
      if (error instanceof DuplicateContactException) {
        if (error.data.includes('email')) {
          useAlert(
            t(
              'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.EMAIL_ADDRESS_DUPLICATE'
            )
          );
        } else if (error.data.includes('phone_number')) {
          useAlert(
            t(
              'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.PHONE_NUMBER_DUPLICATE'
            )
          );
        }
      } else if (error instanceof ExceptionWithMessage) {
        useAlert(error.data);
      } else {
        useAlert(
          t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ERROR_MESSAGE')
        );
      }
    }
  };

  return { open, create };
}
